import { api } from '../../shared/api/client'
import type {
  ApplyChangesResult,
  ApplyJournalBatchResult,
  ApplySourceChangeResult,
  ApproveChangesResult,
  CardChangeSendDetail,
  CardChangeSendSummary,
  CardChangeSendsPage,
  ChangeDetail,
  ChangesPage,
  DetectChangesInput,
  DetectChangesResult,
  DismissSourceChangesResult,
  DiscardChangesResult,
  ExpandJournalBatchResult,
  ExpandSourceChangeResult,
  JournalBatchDetail,
  JournalListItem,
  JournalPage,
  ListCardChangeSendsParams,
  ListChangesParams,
  ListJournalParams,
  SourceChangeSummary,
} from './types'

/** Ответы gateway приходят либо напрямую, либо в стандартном конверте `data`. */
const unwrap = (response: any): any => response?.data?.data ?? response?.data ?? {}

const finiteNumber = (value: unknown, fallback = 0): number => {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

const normalizePage = (response: any): ChangesPage => {
  const data = unwrap(response)
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: finiteNumber(data.total),
    limit: finiteNumber(data.limit, 50),
    offset: finiteNumber(data.offset),
  }
}

const normalizeJournalPage = (response: any): JournalPage => {
  const data = unwrap(response)
  return {
    items: Array.isArray(data.items) ? (data.items.filter(Boolean) as JournalListItem[]) : [],
    total: finiteNumber(data.total),
    limit: finiteNumber(data.limit, 50),
    offset: finiteNumber(data.offset),
  }
}

const toQueryParams = (params: ListChangesParams): Record<string, unknown> => ({
  ...params,
  status: Array.isArray(params.status) ? params.status.join(',') : params.status,
})

export const marketplaceCardChangesAPI = {
  async list(params: ListChangesParams, signal?: AbortSignal): Promise<ChangesPage> {
    const response = await api.get('/cards/changes', { params: toQueryParams(params), signal })
    return normalizePage(response)
  },

  async get(id: number, signal?: AbortSignal): Promise<ChangeDetail> {
    const response = await api.get(`/cards/changes/${id}`, { signal })
    const detail = unwrap(response)
    // Бэкенд отдаёт send_allowed булевым (`mapItemRow` в
    // marketplace-card-changes.repository.ts), экран сравнивает с 0/1: без
    // приведения «Будет отправлено» всегда 0 и «Отправить на площадку» заперта.
    const items = Array.isArray(detail.items)
      ? detail.items.map((item: any) => ({ ...item, send_allowed: Number(item.send_allowed) ? 1 : 0 }))
      : []
    return { ...detail, items } as ChangeDetail
  },

  async detect(input: DetectChangesInput): Promise<DetectChangesResult> {
    const response = await api.post('/cards/changes/detect', input)
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },

  async approve(changeIds: number[]): Promise<ApproveChangesResult> {
    const response = await api.post('/cards/changes/approve', { change_ids: changeIds })
    const data = unwrap(response)
    return {
      approved: Array.isArray(data.approved) ? data.approved : [],
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
    }
  },

  async discard(changeIds: number[], reason?: string): Promise<DiscardChangesResult> {
    const response = await api.post('/cards/changes/discard', {
      change_ids: changeIds,
      ...(reason ? { reason } : {}),
    })
    const data = unwrap(response)
    return {
      discarded: Array.isArray(data.discarded) ? data.discarded : [],
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
    }
  },

  /**
   * Реальная отправка на площадку (POST /api/v1/cards/changes/apply).
   * `publish_confirmed: true` — обязателен всегда: это выход наружу, экшен
   * бэкенда без него отклоняет запрос (см. `applyMarketplaceCardChanges`).
   */
  async apply(changeIds: number[], options?: { dryRun?: boolean }): Promise<ApplyChangesResult> {
    const response = await api.post('/cards/changes/apply', {
      change_ids: changeIds,
      publish_confirmed: true,
      ...(options?.dryRun ? { dry_run: true } : {}),
    })
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },
}

/**
 * Журнал отправок (задача B1, SPEC_B1_send_log.md) — вкладка «Отправки» того
 * же экрана. `GET /api/v1/cards/changes/sends*`, append-only лог ПОПЫТОК
 * отправки (в отличие от `marketplaceCardChangesAPI`, который знает только
 * текущее состояние набора).
 */
const normalizeSendsPage = (response: any): CardChangeSendsPage => {
  const data = unwrap(response)
  return {
    items: Array.isArray(data.items) ? (data.items as CardChangeSendSummary[]) : [],
    total: finiteNumber(data.total),
    limit: finiteNumber(data.limit, 50),
    offset: finiteNumber(data.offset),
  }
}

export const cardChangeSendsAPI = {
  async list(params: ListCardChangeSendsParams, signal?: AbortSignal): Promise<CardChangeSendsPage> {
    const response = await api.get('/cards/changes/sends', { params, signal })
    return normalizeSendsPage(response)
  },

  async get(id: number, signal?: AbortSignal): Promise<CardChangeSendDetail> {
    const response = await api.get(`/cards/changes/sends/${id}`, { signal })
    return unwrap(response) as CardChangeSendDetail
  },
}

/**
 * Журнал правок (contract-journal-v1.md §3, «Правки» на экране `/marketplace/card-changes`).
 * Бэкенд-канал `services/marketplace`, `GET/POST /api/v1/cards/changes/journal*`
 * пишется параллельно (задача J2) — состав путей и тел менять нельзя.
 */
export const journalAPI = {
  /**
   * `group_batches=1` — всегда (§6.2 ТЗ журнала, «Массовая перегенерация —
   * одна строка на партию»): партия массовой перегенерации визуалов бывает
   * 30 000+ записей, экран не должен видеть их по одной строке на запись.
   * `JournalPage.items` при этом — смесь `SourceChangeSummary` и
   * `JournalBatchSummary` (`isJournalBatchSummary`).
   */
  async list(params: ListJournalParams, signal?: AbortSignal): Promise<JournalPage> {
    const response = await api.get('/cards/changes/journal', {
      params: {
        ...params,
        status: Array.isArray(params.status) ? params.status.join(',') : params.status,
        group_batches: 1,
      },
      signal,
    })
    return normalizeJournalPage(response)
  },

  async get(id: number, signal?: AbortSignal): Promise<SourceChangeSummary> {
    const response = await api.get(`/cards/changes/journal/${id}`, { signal })
    return unwrap(response) as SourceChangeSummary
  },

  /**
   * `confirm_affected` обязан совпасть с текущей оценкой затронутых карточек —
   * иначе бэкенд отказывает кодом `affected_mismatch` (contract §3). Вызывающая
   * сторона (`JournalEntryModal`) при этом коде перечитывает актуальное число
   * через `get(id)`, а не повторяет запрос сама.
   */
  async expand(sourceChangeId: number, confirmAffected: number): Promise<ExpandSourceChangeResult> {
    const response = await api.post('/cards/changes/journal/expand', {
      source_change_id: sourceChangeId,
      confirm_affected: confirmAffected,
    })
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },

  async dismiss(sourceChangeIds: number[], reason?: string): Promise<DismissSourceChangesResult> {
    const response = await api.post('/cards/changes/journal/dismiss', {
      source_change_ids: sourceChangeIds,
      ...(reason ? { reason } : {}),
    })
    const data = unwrap(response)
    return {
      dismissed: Array.isArray(data.dismissed) ? data.dismissed : [],
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
    }
  },

  /** Одно подтверждение на правку — отправляет все её наборы с разрешёнными к отправке позициями (contract §3). */
  async apply(sourceChangeId: number, options?: { dryRun?: boolean }): Promise<ApplySourceChangeResult> {
    const response = await api.post('/cards/changes/journal/apply', {
      source_change_id: sourceChangeId,
      publish_confirmed: true,
      ...(options?.dryRun ? { dry_run: true } : {}),
    })
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },

  /**
   * ─────────────── Партии массовой перегенерации визуалов (Этап 5, §6.2) ───────────────
   * `GET/POST /api/v1/cards/changes/journal/batches/:batchRef*` — сводка и
   * разворот/отправка партии одним подтверждением. Список записей партии на
   * фронт не запрашивается: экран работает только со сводкой
   * (`getBatchSummary`/`resolveAffectedCountForBatch` на бэкенде).
   */

  /** Сводка партии для карточки партии — `affected_estimate` считается на бэкенде под запрос, не в списке. */
  async getBatch(batchRef: string, signal?: AbortSignal): Promise<JournalBatchDetail> {
    const response = await api.get(`/cards/changes/journal/batches/${encodeURIComponent(batchRef)}`, { signal })
    return unwrap(response) as JournalBatchDetail
  },

  /**
   * `expected_count` обязан совпасть с текущим числом записей партии в
   * статусе `pending` — тот же смысл, что `confirm_affected` у одиночного
   * разворота, только отказ приходит `409 CONFLICT` (contract §7.2/задача J2:
   * `getErrorStatusCode`), а не `affected_mismatch`. Вызывающая сторона
   * (`JournalBatchModal`) при этом коде перечитывает сводку партии заново.
   */
  async expandBatch(batchRef: string, expectedCount: number): Promise<ExpandJournalBatchResult> {
    const response = await api.post(`/cards/changes/journal/batches/${encodeURIComponent(batchRef)}/expand`, {
      expected_count: expectedCount,
    })
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },

  /** Одно подтверждение на партию — отправляет наборы, развёрнутые из всех её записей. */
  async applyBatch(batchRef: string, options?: { dryRun?: boolean }): Promise<ApplyJournalBatchResult> {
    const response = await api.post(`/cards/changes/journal/batches/${encodeURIComponent(batchRef)}/apply`, {
      publish_confirmed: true,
      ...(options?.dryRun ? { dry_run: true } : {}),
    })
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' ? data.jobId.trim() : ''
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return { jobId }
  },
}
